import { useCallback, useMemo, useState } from 'react';
import { Link, useSearchParams } from 'react-router-dom';

import { Card, CardContent } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { adminApi, type AdminApplicationRow } from '@/services/adminApi';
import { useAdminExportCsv } from '@/hooks/admin/useAdminExportCsv';
import { useAdminCursorList } from '@/hooks/admin/useAdminCursorList';
import { AdminListHeader, ActiveFiltersBar } from '@/components/admin/AdminListToolbar';
import { AdminListFooter } from '@/components/admin/AdminListFooter';
import { SortableTableHeader, type SortDir } from '@/components/admin/SortableTableHeader';

/**
 * AdminApplicationsPage — cross-tenant collectionGroup view on
 * `jobApplications`. Critical filter: `lastFailureReason=billing_limit_reached`
 * to triage Pending Billing applications across all customers in one
 * place. Plus state, channel, candidate email, applied date range.
 */
export default function AdminApplicationsPage() {
  const [searchParams] = useSearchParams();
  const [state, setState] = useState('all');
  const [lastFailureReason, setLastFailureReason] = useState('all');
  const [companyId, setCompanyId] = useState(searchParams.get('companyId') ?? '');
  const [channel, setChannel] = useState('all');
  const [candidateEmail, setCandidateEmail] = useState('');
  const [appliedFrom, setAppliedFrom] = useState('');
  const [appliedTo, setAppliedTo] = useState('');
  const [searchInline, setSearchInline] = useState('');
  const [sortBy, setSortBy] = useState<string | null>(null);
  const [sortDir, setSortDir] = useState<SortDir>('desc');
  const onSortChange = (field: string | null, dir: SortDir) => { setSortBy(field); setSortDir(dir); };

  const filterParams = useMemo(() => ({
    ...(state !== 'all' ? { state } : {}),
    ...(lastFailureReason !== 'all' ? { lastFailureReason } : {}),
    ...(companyId ? { companyId } : {}),
    ...(channel !== 'all' ? { channel } : {}),
    ...(candidateEmail ? { candidateEmail } : {}),
    ...(appliedFrom ? { appliedFrom } : {}),
    ...(appliedTo ? { appliedTo } : {}),
    ...(sortBy ? { sortBy, sortDir } : {}),
  }), [state, lastFailureReason, companyId, channel, candidateEmail, appliedFrom, appliedTo, sortBy, sortDir]);

  const { exportCsv, isExporting } = useAdminExportCsv('applications', filterParams);

  const fetchPage = useCallback(
    (cursor: string | null) => adminApi.applications.list({ ...filterParams, cursor }),
    [filterParams],
  );
  const {
    items, totalCount, loading, isRefreshing, error, refresh,
    hasMore, loadingMore, loadMore,
  } = useAdminCursorList<AdminApplicationRow>(fetchPage, [filterParams]);

  const filtered = useMemo(() => {
    if (!searchInline) return items;
    const s = searchInline.toLowerCase();
    return items.filter((r) =>
      r.candidateName.toLowerCase().includes(s)
      || r.candidateEmail.toLowerCase().includes(s)
      || (r.companyName ?? '').toLowerCase().includes(s)
      || (r.positionTitle ?? '').toLowerCase().includes(s)
      || (r.companyId ?? '').toLowerCase().includes(s),
    );
  }, [items, searchInline]);

  const activeFilters: Array<{ key: string; label: string; onClear: () => void }> = [];
  if (state !== 'all') activeFilters.push({ key: 'state', label: `State: ${state}`, onClear: () => setState('all') });
  if (lastFailureReason !== 'all') activeFilters.push({ key: 'failure', label: `Failure: ${lastFailureReason}`, onClear: () => setLastFailureReason('all') });
  if (companyId) activeFilters.push({ key: 'company', label: `Company: ${companyId.slice(0, 10)}…`, onClear: () => setCompanyId('') });
  if (channel !== 'all') activeFilters.push({ key: 'channel', label: `Channel: ${channel}`, onClear: () => setChannel('all') });
  if (candidateEmail) activeFilters.push({ key: 'cand-email', label: `Email: ${candidateEmail}`, onClear: () => setCandidateEmail('') });
  if (appliedFrom) activeFilters.push({ key: 'from', label: `From: ${appliedFrom}`, onClear: () => setAppliedFrom('') });
  if (appliedTo) activeFilters.push({ key: 'to', label: `To: ${appliedTo}`, onClear: () => setAppliedTo('') });
  if (searchInline) activeFilters.push({ key: 'search', label: `Search: "${searchInline}"`, onClear: () => setSearchInline('') });

  const clearAll = () => {
    setState('all'); setLastFailureReason('all'); setCompanyId('');
    setChannel('all'); setCandidateEmail(''); setAppliedFrom(''); setAppliedTo(''); setSearchInline('');
  };

  return (
    <div className="space-y-4 max-w-6xl">
      <AdminListHeader
        title="Applications"
        onRefresh={refresh}
        isRefreshing={isRefreshing}
        onExportCsv={exportCsv}
        isExporting={isExporting}
      />

      <div className="flex flex-wrap items-center gap-2">
        <Input
          placeholder="Search loaded rows (candidate, company, position)..."
          value={searchInline}
          onChange={(e) => setSearchInline(e.target.value)}
          className="h-9 w-[300px]"
        />
        <Select value={state} onValueChange={setState}>
          <SelectTrigger className="h-9 w-[180px]"><SelectValue placeholder="State" /></SelectTrigger>
          <SelectContent>
            <SelectItem value="all">Any state</SelectItem>
            <SelectItem value="NEW">NEW</SelectItem>
            <SelectItem value="PENDING_UPGRADE">PENDING_UPGRADE</SelectItem>
            <SelectItem value="READY_TO_PROCESS">READY_TO_PROCESS</SelectItem>
            <SelectItem value="SCREENING">SCREENING</SelectItem>
            <SelectItem value="AWAITING_DECISION">AWAITING_DECISION</SelectItem>
            <SelectItem value="INTERVIEW">INTERVIEW</SelectItem>
            <SelectItem value="OFFER">OFFER</SelectItem>
            <SelectItem value="HIRED">HIRED</SelectItem>
            <SelectItem value="REJECTED">REJECTED</SelectItem>
            <SelectItem value="WITHDRAWN">WITHDRAWN</SelectItem>
            <SelectItem value="PIPELINE_ERROR">PIPELINE_ERROR</SelectItem>
          </SelectContent>
        </Select>
        <Select value={lastFailureReason} onValueChange={setLastFailureReason}>
          <SelectTrigger className="h-9 w-[200px]"><SelectValue placeholder="Failure reason" /></SelectTrigger>
          <SelectContent>
            <SelectItem value="all">Any failure reason</SelectItem>
            <SelectItem value="billing_limit_reached">Billing limit reached</SelectItem>
            <SelectItem value="system_error">System error</SelectItem>
          </SelectContent>
        </Select>
        <Select value={channel} onValueChange={setChannel}>
          <SelectTrigger className="h-9 w-[140px]"><SelectValue placeholder="Channel" /></SelectTrigger>
          <SelectContent>
            <SelectItem value="all">Any channel</SelectItem>
            <SelectItem value="company_website">Website</SelectItem>
            <SelectItem value="linkedin">LinkedIn</SelectItem>
            <SelectItem value="referral">Referral</SelectItem>
            <SelectItem value="other">Other</SelectItem>
          </SelectContent>
        </Select>
        <Input
          placeholder="Company ID..."
          value={companyId}
          onChange={(e) => setCompanyId(e.target.value)}
          className="h-9 w-[220px] font-mono text-xs"
        />
        <Input
          placeholder="Candidate email contains..."
          value={candidateEmail}
          onChange={(e) => setCandidateEmail(e.target.value)}
          className="h-9 w-[240px]"
        />
        <div className="flex items-center gap-1">
          <span className="text-xs text-muted-foreground">Applied</span>
          <Input type="date" value={appliedFrom} onChange={(e) => setAppliedFrom(e.target.value)} className="h-9 w-[150px]" />
          <span className="text-xs text-muted-foreground">→</span>
          <Input type="date" value={appliedTo} onChange={(e) => setAppliedTo(e.target.value)} className="h-9 w-[150px]" />
        </div>
      </div>

      <ActiveFiltersBar filters={activeFilters} onClearAll={clearAll} />

      {error && <p className="text-sm text-destructive">{error}</p>}

      <Card className="border-border/50">
        <CardContent className="p-0">
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="border-b border-border/50">
                  <th className="text-left font-medium text-muted-foreground py-3 px-4">Candidate</th>
                  <th className="text-left font-medium text-muted-foreground py-3 px-3">Company</th>
                  <th className="text-left font-medium text-muted-foreground py-3 px-3">Position</th>
                  <th className="text-left py-3 px-3">
                    <SortableTableHeader label="State" field="state" sortBy={sortBy} sortDir={sortDir} onChange={onSortChange} defaultDir="asc" />
                  </th>
                  <th className="text-left font-medium text-muted-foreground py-3 px-3">Failure</th>
                  <th className="text-left py-3 px-3">
                    <SortableTableHeader label="Channel" field="channel" sortBy={sortBy} sortDir={sortDir} onChange={onSortChange} defaultDir="asc" />
                  </th>
                  <th className="text-left py-3 px-3">
                    <SortableTableHeader label="Applied" field="appliedAt" sortBy={sortBy} sortDir={sortDir} onChange={onSortChange} />
                  </th>
                </tr>
              </thead>
              <tbody>
                {loading && <tr><td colSpan={7} className="py-12 text-center text-muted-foreground">Loading...</td></tr>}
                {!loading && filtered.length === 0 && (
                  <tr><td colSpan={7} className="py-12 text-center text-muted-foreground">No applications.</td></tr>
                )}
                {filtered.map((row) => {
                  const detailHref = row.companyId && row.jobPositionId
                    ? `/admin/customers/${row.companyId}/positions/${row.jobPositionId}/applications/${row.applicationId}`
                    : null;
                  return (
                    <tr key={row.applicationId} className="border-b border-border/30 last:border-0 hover:bg-accent/40">
                      <td className="py-3 px-4">
                        {detailHref ? (
                          <Link to={detailHref} className="font-medium hover:underline">
                            {row.candidateName || '(unknown)'}
                          </Link>
                        ) : (
                          <span className="font-medium">{row.candidateName || '(unknown)'}</span>
                        )}
                        <p className="text-xs text-muted-foreground truncate max-w-[240px]">{row.candidateEmail}</p>
                      </td>
                      <td className="py-3 px-3">
                        {row.companyId ? (
                          <Link to={`/admin/customers/${row.companyId}`} className="hover:underline text-foreground" title={row.companyId}>
                            {row.companyName || row.companyId.slice(0, 10) + '…'}
                          </Link>
                        ) : '—'}
                      </td>
                      <td className="py-3 px-3 text-muted-foreground truncate max-w-[200px]">
                        {row.positionTitle || '—'}
                      </td>
                      <td className="py-3 px-3">
                        <span className="text-xs px-2 py-0.5 rounded-md bg-muted">{row.state}</span>
                      </td>
                      <td className="py-3 px-3">
                        {row.lastFailureReason ? (
                          <span className={`text-xs px-2 py-0.5 rounded-md ${
                            row.lastFailureReason === 'billing_limit_reached'
                              ? 'bg-amber-100 text-amber-900'
                              : 'bg-destructive/10 text-destructive'
                          }`}>{row.lastFailureReason}</span>
                        ) : '—'}
                      </td>
                      <td className="py-3 px-3 text-muted-foreground">{row.channel || '—'}</td>
                      <td className="py-3 px-3 text-muted-foreground text-xs">
                        {row.appliedAt ? new Date(row.appliedAt).toLocaleString('en-GB') : '—'}
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
          <AdminListFooter
            loadedCount={items.length}
            filteredCount={searchInline ? filtered.length : undefined}
            totalCount={totalCount}
            entityLabel="candidatura"
            entityLabelPlural="candidature"
            hasMore={hasMore}
            loadingMore={loadingMore}
            onLoadMore={loadMore}
          />
        </CardContent>
      </Card>
      {isRefreshing && <p className="text-xs text-muted-foreground text-right">Aggiornamento...</p>}
    </div>
  );
}
